import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { ContactDetails, hasContactDetails } from '@/components/generals/contact-details'
import { siteConfig } from '@/constants/site'
import { FOOTER_EXPLORE } from '@/constants/navigation'

const BUYER_LINKS = [
    { label: 'Request a quote', href: '/contact' },
    { label: 'Specification sheets', href: '/products' },
    { label: 'Order terms', href: '/#faq' }
]

const linkClass = 'rounded-sm text-sm text-background/75 transition-colors hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer id='site-footer' className='bg-foreground text-background'>
            <Container className='py-16 lg:py-20'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <Logo tone='dark' />

                        <p className='mt-6 max-w-sm text-sm leading-relaxed text-background/70'>
                            {siteConfig.description}
                        </p>

                        <Link
                            href='/contact'
                            className={cn(buttonVariants({ variant: 'gold', size: 'sm' }), 'mt-8')}
                        >
                            Request a quote
                            <ArrowRight aria-hidden='true' />
                        </Link>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3 lg:col-span-7'>
                        <nav aria-labelledby='footer-explore'>
                            <p id='footer-explore' className='text-xs font-bold tracking-[0.2em] text-gold uppercase'>
                                Explore
                            </p>

                            <ul className='mt-5 space-y-3'>
                                {FOOTER_EXPLORE.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className={linkClass}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <nav aria-labelledby='footer-buyers'>
                            <p id='footer-buyers' className='text-xs font-bold tracking-[0.2em] text-gold uppercase'>
                                For buyers
                            </p>

                            <ul className='mt-5 space-y-3'>
                                {BUYER_LINKS.map(link => (
                                    <li key={link.label}>
                                        <Link href={link.href} className={linkClass}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div>
                            <p className='text-xs font-bold tracking-[0.2em] text-gold uppercase'>
                                Contact
                            </p>

                            {hasContactDetails() ? (
                                <ContactDetails tone='dark' className='mt-5' />
                            ) : (
                                <p className='mt-5 text-sm leading-relaxed text-background/75'>
                                    Send us the variety, quantity and packing you need, and our trade desk will reply with a quotation.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-background/15 pt-8 text-sm text-background/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Basmati, non-basmati, parboiled and broken rice in bulk
                    </p>
                </div>
            </Container>
        </footer>
    )
}
